import { Avatar, Grid, Link, makeStyles, Paper, Typography } from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import chroma from "chroma-js";
import cx from "classnames";
import { groupBy, sortBy } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import SponsoredSession from "../../assets/images/sponsored_session.png";
import Loader from "../../components/Loader";
import { getProgramEvents } from "../../ducks/actions/program";
import * as types from "../../ducks/types";
import { dateTimeInterval } from "../../ducks/utils";
import useSyncInterval from "../../hooks/useSyncInterval";

const useStyles = makeStyles(theme => ({
  event: {
    position: "relative",
    minHeight: theme.spacing(12),
    borderRadius: theme.spacing(0.5),
    margin: theme.spacing(1),
    padding: theme.spacing(1)
  },
  clickable: {
    cursor: "pointer"
  },
  pastEvent: {
    color: "#B5B5C0"
  },
  row: {
    padding: theme.spacing(2)
  },
  darkRow: {
    backgroundColor: chroma(theme.palette.text.primary).alpha(0.2).hex(),
    borderRadius: theme.spacing(1)
  },
  roomEventsContainer: {
    paddingBottom: theme.spacing(2)
  },
  liveNow: {
    color: "#FF0033",
    textTransform: "uppercase"
  },
  sponsoredSession: {
    position: "absolute",
    right: -4,
    top: -4,
    width: 40,
    height: 51
  }
}));

const ProgramSlot = ({ startAt, endAt, label, index, onSessionOpen }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const classes = useStyles();

  useEffect(() => {
    dispatch(getProgramEvents({ startAt, endAt }));
  }, [dispatch, startAt, endAt]);

  const [isPolling, setIsPolling] = useState(false);
  useSyncInterval(() => {
    setIsPolling(true);
    dispatch(getProgramEvents({ startAt, endAt }));
  });

  const {
    data: { data: programEvents },
    loading
  } = useQuery({
    type: types.GET_PROGRAM_EVENTS,
    requestKey: `${startAt}${endAt}`,
    defaultData: { data: [] }
  });

  const programEventsByRooms = groupBy(
    programEvents.map(pe => {
      if (moment(pe.endAt).add(30, "minutes").isBefore()) {
        return { ...pe, isPast: true };
      }
      if (moment(pe.startAt).subtract(30, "minutes").isBefore()) {
        return { ...pe, isLive: true };
      }
      return pe;
    }),
    ({ roomList }) => roomList.join()
  );
  return (
    <div className={cx(classes.row, { [classes.darkRow]: index % 2 })}>
      <Grid container>
        <Grid item xs={12} lg={2} xl={1}>
          <Typography variant="h5">
            <strong>{label}</strong>
          </Typography>
        </Grid>
        <Grid item container xs={12} lg={10} xl={11}>
          <Loader loading={loading && !isPolling} circular>
            {Object.keys(programEventsByRooms)
              .sort()
              .map(room => (
                <Grid
                  className={classes.roomEventsContainer}
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  xl={3}
                  key={room}
                >
                  {sortBy(programEventsByRooms[room], ["startAt"]).map(
                    ({ id, label, startAt, endAt, roomList, eventable, isPast, isLive }) => (
                      <Paper
                        key={id}
                        onClick={onSessionOpen({ id: eventable?.id || null })}
                        className={cx(classes.event, {
                          [classes.pastEvent]: isPast,
                          [classes.clickable]: Boolean(eventable)
                        })}
                      >
                        {eventable?.exhibitors && (
                          <Avatar
                            src={SponsoredSession}
                            alt="Sponsored Session"
                            variant="square"
                            className={classes.sponsoredSession}
                          />
                        )}

                        <Typography variant="h6">
                          <strong>{label}</strong>
                        </Typography>
                        <Typography variant="body2">
                          {intl.formatMessage(
                            {
                              id: "Program.ProgramSlot.Room",
                              defaultMessage: "Room {room}"
                            },
                            { room: roomList.join(", ") }
                          )}
                          &nbsp;&#8226;&nbsp;
                          {isLive ? (
                            <strong className={classes.liveNow}>
                              {intl.formatMessage({
                                id: "Program.ProgramSlot.LiveNow",
                                defaultMessage: "Live now"
                              })}
                            </strong>
                          ) : (
                            dateTimeInterval(startAt, endAt, false)
                          )}
                        </Typography>
                        {isLive && eventable?.zoomWebinarJoinUrl && (
                          <Typography variant="overline">
                            <Link to={eventable?.zoomWebinarJoinUrl} target="_blank">
                              {intl.formatMessage({
                                id: "Program.ProgramSlot.JoinRoom",
                                defaultMessage: "Join room"
                              })}
                            </Link>
                          </Typography>
                        )}
                        {!isLive && eventable?.recordings?.length && (
                          <Typography variant="overline">
                            <Link
                              component={RouterLink}
                              to={`/sessions/${eventable?.id}`}
                              onClick={e => e.stopPropagation()}
                            >
                              {intl.formatMessage({
                                id: "Program.ProgramSlot.WatchTheVideo",
                                defaultMessage: "Watch the video"
                              })}
                            </Link>
                          </Typography>
                        )}
                      </Paper>
                    )
                  )}
                </Grid>
              ))}
          </Loader>
        </Grid>
      </Grid>
    </div>
  );
};

export default ProgramSlot;
